<template>
  <div>
    <div v-if="!moduleAvailable" class="flex flex-col items-center justify-center py-24 text-center px-4">
      <p class="text-sm text-gray-500 dark:text-gray-400">Chart module failed to load.</p>
    </div>
    <div v-else ref="chartEl" class="w-full" :style="{ height: `${chartHeight}px` }"></div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { NODE_ICONS } from 'controllers/vue_components/organizations/structure/nodeIcons.js'

const props = defineProps({
  companies: { type: Array, required: true },
  orgName: { type: String, default: 'Organization' },
})

const chartEl = ref(null)
const moduleAvailable = computed(() => !!window.Highcharts?.seriesTypes?.treegraph)

let chart = null

// Same icon paths as TreeRow.vue (via the shared nodeIcons module), so table view and chart view stay visually consistent.
// Colors follow the app design system's blue accent scale (blue-900 / blue-600 / blue-50).
const NODE_STYLES = {
  org: { ...NODE_ICONS.org, fill: '#1e3a8a', iconColor: '#ffffff', textColor: '#ffffff' },
  company: { ...NODE_ICONS.company, fill: '#2563eb', iconColor: '#ffffff', textColor: '#ffffff' },
  department: { ...NODE_ICONS.department, fill: '#eff6ff', iconColor: '#2563eb', textColor: '#1e3a8a' },
}

const nodeCount = computed(() => {
  let count = 1
  for (const company of props.companies) {
    count += 1 + (company.departments || []).length
  }
  return count
})

const chartHeight = computed(() => Math.max(360, Math.min(900, nodeCount.value * 30)))

const treeData = computed(() => {
  const nodes = [{ id: 'org-root', parent: '', name: props.orgName, custom: { type: 'org' } }]
  for (const company of props.companies) {
    const companyId = `c-${company.id}`
    nodes.push({ id: companyId, parent: 'org-root', name: company.name, custom: { type: 'company' } })
    for (const department of company.departments || []) {
      nodes.push({ id: `d-${department.id}`, parent: companyId, name: department.name, custom: { type: 'department' } })
    }
  }
  return nodes
})

function labelFormatter() {
  const style = NODE_STYLES[this.point.custom?.type] || NODE_STYLES.department
  return `<div style="display:flex;align-items:center;gap:6px;white-space:nowrap;">`
    + `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="${style.iconColor}" stroke-width="1.75">`
    + `<path stroke-linecap="round" stroke-linejoin="round" d="${style.path}"/></svg>`
    + `<span style="color:${style.textColor};font-size:12px;font-weight:600;">${this.point.name}</span>`
    + `</div>`
}

function tooltipFormatter() {
  const label = NODE_STYLES[this.custom?.type]?.label
  return label ? `${this.name} (${label})` : this.name
}

function renderChart() {
  if (!chartEl.value || !moduleAvailable.value) return
  if (chart) { chart.destroy(); chart = null }

  chart = window.Highcharts.chart(chartEl.value, {
    chart: { height: chartHeight.value, spacing: [16, 16, 16, 16], backgroundColor: 'transparent' },
    title: { text: undefined },
    credits: { enabled: false },
    exporting: { enabled: false },
    series: [{
      type: 'treegraph',
      data: treeData.value,
      tooltip: { pointFormatter: tooltipFormatter, linkFormat: '{point.fromNode.name} → {point.toNode.name}' },
      marker: { symbol: 'rect', width: '26%' },
      borderRadius: 8,
      link: { type: 'curved', color: '#e5e7eb', lineWidth: 1.5 },
      dataLabels: {
        useHTML: true,
        align: 'center',
        verticalAlign: 'middle',
        formatter: labelFormatter,
      },
      levels: [
        { level: 1, color: NODE_STYLES.org.fill },
        { level: 2, color: NODE_STYLES.company.fill },
        { level: 3, color: NODE_STYLES.department.fill, borderColor: '#bfdbfe', borderWidth: 1 },
      ],
    }],
  })
}

watch(() => props.companies, () => nextTick(renderChart), { deep: true })
onMounted(() => nextTick(renderChart))
onUnmounted(() => { if (chart) { chart.destroy(); chart = null } })
</script>
